import { BOARD_TEXT_FONT, normalizeTextFont } from "./textSession";

/**
 * Шрифты надписей на доске.
 *
 * Только с кириллицей: иначе русский текст показывался бы запасным шрифтом,
 * и у разных людей строки снова переносились бы по-разному. Все — с Google
 * Fonts (лицензия OFL); стили подключены в index.html, а сами файлы шрифта
 * браузер скачивает, только когда надпись этим шрифтом реально есть на
 * экране, — длинный список страницу не утяжеляет.
 */

export type BoardFontKind = "sans" | "serif" | "hand" | "mono";

export type BoardFont = {
  /** Имя семейства — как в CSS и в Google Fonts. */
  family: string;
  /** Подпись в списке. */
  label: string;
  /** Что пишем в style.fontFamily надписи: семейство и запасные. */
  css: string;
  kind: BoardFontKind;
};

const sans = (family: string, label = family): BoardFont => ({
  family,
  label,
  css: `"${family}", system-ui, sans-serif`,
  kind: "sans",
});

export const BOARD_FONTS: readonly BoardFont[] = [
  { family: "Onest", label: "Onest — как на сайте", css: BOARD_TEXT_FONT, kind: "sans" },
  sans("PT Sans"),
  sans("Montserrat"),
  sans("Rubik"),
  sans("Comfortaa"),
  { family: "PT Serif", label: "PT Serif", css: '"PT Serif", Georgia, serif', kind: "serif" },
  { family: "Lora", label: "Lora", css: '"Lora", Georgia, serif', kind: "serif" },
  { family: "Caveat", label: "Caveat — рукописный", css: '"Caveat", cursive', kind: "hand" },
  { family: "Neucha", label: "Neucha — рукописный", css: '"Neucha", cursive', kind: "hand" },
  {
    family: "JetBrains Mono",
    label: "JetBrains Mono — моноширинный",
    css: '"JetBrains Mono", ui-monospace, monospace',
    kind: "mono",
  },
];

/** Первое имя в строке шрифтов, без кавычек. */
function firstFamily(css: string): string {
  const first = css.split(",")[0] ?? "";
  return first.trim().replace(/^["']|["']$/g, "");
}

/** Какой шрифт из списка у надписи. Незнакомый — шрифт доски. */
export function fontOfStyle(fontFamily: string | null | undefined): BoardFont {
  const family = firstFamily(normalizeTextFont(fontFamily)).toLowerCase();
  return BOARD_FONTS.find((f) => f.family.toLowerCase() === family) ?? BOARD_FONTS[0]!;
}

/**
 * Дождаться файлов шрифта, прежде чем мерить надпись.
 *
 * Пока шрифт не скачан, браузер рисует запасным, и рамка измерилась бы по
 * нему — у всех остальных надпись потом не влезала бы в свою рамку.
 * Дольше трёх секунд не ждём: связь бывает плохой, а надпись нужна сейчас.
 */
export async function ensureFontLoaded(font: BoardFont): Promise<void> {
  if (typeof document === "undefined" || !document.fonts) return;
  const sample = "Аа Яя Zz 0";
  const loads = Promise.all([
    document.fonts.load(`400 18px "${font.family}"`, sample),
    document.fonts.load(`700 18px "${font.family}"`, sample),
  ]).then(() => undefined);
  const timeout = new Promise<void>((resolve) => setTimeout(resolve, 3000));
  await Promise.race([loads.catch(() => undefined), timeout]);
}
